import React, { useEffect, useState } from "react";
import { BetaForm as useBetaForm } from "@reusejs/react";
import { Input } from "../../../../../../shadcn/components/ui/input";
import { Button } from "../../../../../../shadcn/components/ui/button";
import { Dropdown } from "../../../../../../shadcn/components/custom/Dropdown";
import { useSelector } from "react-redux";
import { getBidderByUuid, updateBidderForEvent } from "services";
import { Label } from "../../../../../../shadcn/components/ui/label";
import CustomLoadingModal from "./CustomLoadingModal";
import ExclamationIcon from "../../../../../../icons/ExclamationIcon";
interface BidApplicationFormWithOutPDProps {
  stepsTitles: any;
  activeStep: any;
  setActiveStep: any;
  bidderUuid: any;
  init: any;
  bidder: any;
  outerNextStep?: string | null;
}

export default function BidApplicationFormWithOutPD({
  bidderUuid,
  init,
  stepsTitles,
  activeStep,
  setActiveStep,
  bidder,
  outerNextStep = null,
}: BidApplicationFormWithOutPDProps) {
  const onboardingData = useSelector((state: any) => {
    return state.bidderAuth.onboarding;
  });
  const [loading, setLoading] = useState(false);
  const [kycFiles, setKycFiles] = useState<any>([]);
  const [renderKey, setRenderKey] = useState(0);

  const form = useBetaForm({
    email: "",
    mobile: "",
    pan: "",
    name: "",
    event_id: "",
    emd_amount: "",
    bid_amount: "",
    relationship: "",
  });
  const form_constraints = {
    mobile: {
      presence: {
        allowEmpty: false,
      },
      length: {
        maximum: 10,
        message: "^Mobile number is too long (maximum is 10 characters)",
      },
    },
    email: {
      presence: {
        allowEmpty: false,
      },
    },
    pan: {
      presence: {
        allowEmpty: false,
      },
      length: {
        maximum: 10,
      },
    },
    name: {
      presence: {
        allowEmpty: true,
      },
    },
    event_id: {
      presence: { allowEmpty: false },
    },
    emd_amount: {
      presence: { allowEmpty: false },
      numericality: {
        greaterThan: 0,
        message: "^EMD amount must be a positive number",
      },
    },
    bid_amount: {
      presence: {
        allowEmpty: false,
      },
      numericality: {
        onlyInteger: true,
        greaterThanOrEqualTo:
          typeof bidder?.meta?.reserve_price === "string"
            ? parseInt(bidder?.meta?.reserve_price)
            : bidder?.meta?.reserve_price,
        message: `^Bid amount must be greater than or equal to reserve price:${bidder?.meta?.reserve_price}`,
      },
    },
  };
  useEffect(() => {
    form.setValidationRules(form_constraints);
  }, [form?.value]);

  const types = [
    { label: "Yes", value: "yes" },
    { label: "No", value: "no" },
  ];

  useEffect(() => {
    const applicationFormData = bidder?.application_form_data?.form_data;
    if (applicationFormData) {
      form.setField("email", applicationFormData?.email || "");
      form.setField("mobile", applicationFormData?.mobile || "");
      form.setField("pan", applicationFormData?.pan || "");
      form.setField("name", applicationFormData?.name || "");
      form.setField("event_id", applicationFormData?.event_id || "");
      form.setField("emd_amount", applicationFormData?.emd_amount || "");
      form.setField("bid_amount", applicationFormData?.bid_amount || "");
      form.setField("relationship", applicationFormData?.relationship || "");
    } else {
      form.setField("email", bidder?.email || "");
      form.setField("mobile", bidder?.meta?.user_details?.mobile_number || "");
      form.setField("pan", onboardingData?.pan || "");
      form.setField("name", onboardingData?.name || "");
      form.setField("event_id", bidder?.meta?.event_id || "");
      form.setField("emd_amount", bidder?.meta?.emd_amount || "");
      form.setField("relationship", "");
    }
  }, [bidder, bidderUuid, onboardingData]);

  const handleSubmit = async () => {
    form.setField("emd_proof_uuid", kycFiles);
    console.log("handleSubmit-----", form.validate(), form.errors);
    if (form.validate()) {
      setLoading(true);
      const data: any = await getBidderByUuid(bidderUuid);
      await updateBidderForEvent({
        uuid: bidderUuid,
        meta: {
          ...data?.meta,
          steps_status: {
            ...data?.meta?.steps_status,
            [outerNextStep === null ? "bid_details" : outerNextStep]:
              "completed",
          },
        },
        application_form_data: {
          ...data?.application_form_data,
          form_data: {
            ...data?.application_form_data?.form_data,
            ...form?.value,
          },
          status: "completed",
          // Clear rejection-related fields
          reject_remarks: null,
        },
        verification_step: outerNextStep || "bid_details",
        status: "pending",
      });
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setActiveStep(activeStep + 1);
      await init();
      setLoading(false);
    }
  };

  return (
    <div>
      {bidder?.application_form_data?.status === "completed" ? (
        <div className="text-sm">This step is completed.</div>
      ) : (
        <div>
          {bidder?.application_form_data?.status === "rejected" && (
            <div className="mb-6">
              <div className="border-[1px] border-red-300 bg-red-50 px-4 py-3 rounded-md">
                <div className="flex gap-2 md:gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <ExclamationIcon className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm md:text-base font-semibold text-red-700 mb-2">
                      Application Rejected
                    </div>
                    <div className="text-xs md:text-sm text-red-600 mb-2">
                      <span className="font-semibold">Rejection Remarks:</span>{" "}
                      {bidder?.application_form_data?.reject_remarks ||
                        "No rejection remarks provided"}
                    </div>
                    <div className="text-xs md:text-sm text-red-700 leading-relaxed">
                      Please review the remarks above and update your bid
                      application details accordingly.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="text-xs md:text-sm text-brand-textGrey mt-2 md:mt-4 mb-4 md:mb-8">
            <strong className="text-brand-textBlack">Property:</strong>{" "}
            {bidder?.meta?.event_name}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label htmlFor="email">Email*</Label>
              <Input
                disabled={true}
                placeholder="Email*"
                value={form.getField("email")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("email") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("email")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="mobile">Mobile*</Label>
              <Input
                disabled={true}
                type="number"
                placeholder={"Mobile*"}
                value={form.getField("mobile")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("mobile") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("mobile")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="pan">PAN Number*</Label>
              <Input
                disabled={true}
                placeholder={"PAN Number*"}
                value={form.getField("pan")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("pan") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("pan")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-brand-bgBlack" htmlFor="name">
                Name*
              </Label>
              <Input
                disabled={true}
                placeholder="Name*"
                value={form.getField("name")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("name") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("name")?.[0]}
                </p>
              ) : null}
            </div>
          </div>
          <div className="py-8 font-semibold">Bid Details</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
              <Label className="text-brand-bgBlack" htmlFor="event_id">
                Listing Id*
              </Label>
              <Input
                disabled={true}
                placeholder="Listing Id*"
                value={form.getField("event_id")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("event_id") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("event_id")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-brand-bgBlack" htmlFor="emd_amount">
                EMD Amount*
              </Label>
              <Input
                disabled={true}
                placeholder={"EMD Amount*"}
                value={form.getField("emd_amount")}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("emd_amount") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("emd_amount")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label htmlFor="bid_amount">Bid Amount*</Label>
              <Input
                type="number"
                placeholder="Bid Amount*"
                value={form.getField("bid_amount")}
                onChange={(e: any) => {
                  const rawValue = e?.target?.value;
                  if (rawValue === "") {
                    form.setField("bid_amount", "");
                  } else {
                    const numericValue = Number(rawValue);
                    const value = isNaN(numericValue)
                      ? ""
                      : Math.abs(numericValue);
                    form.setField("bid_amount", value);
                  }
                }}
                className="border-[1px] border-brand-borderLightGray w-full py-2 px-4 text-sm tracking-wide font-sans text-brand-textBlack rounded"
              />
              {form.errors.get("bid_amount") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("bid_amount")?.[0]}
                </p>
              ) : null}
            </div>
            <div>
              <Label className="text-brand-bgBlack" htmlFor="emd_amount">
                Relationship With AB*
              </Label>
              <Dropdown
                dataSource={() => {
                  return Promise.resolve(types);
                }}
                defaultSelected={
                  form.getField("relationship") !== ""
                    ? form.getField("relationship")
                    : null
                }
                selectIdentifier={true}
                onChange={async (value: any) => {
                  form.setField("relationship", value);
                }}
                valueKey="value"
                placeholder="Relationship with Seller/E-Auction Vendor*"
                styles={{
                  hideDot: true,
                  dropdownItemsClass: "text-brand-borderGrey",
                  hoverBgColor: "focus:bg-brand-lightPink",
                  hoverTextColor: "focus:text-brand-borderGrey",
                  outerContainer:
                    "border-[1px] border-brand-borderLightGray rounded-sm text-xs sm:text-sm leading-normal font-sans text-brand-textGrey px-2 sm:px-4 h-[38px]",
                }}
              />
              {form.errors.get("relationship") ? (
                <p className="mt-1 text-sm text-red-500">
                  {form.errors.get("relationship")[0]}
                </p>
              ) : null}
            </div>
          </div>
          <div className="mt-8 mb-10 md:mb-0 flex justify-center">
            <Button
              disabled={loading}
              className="w-full max-w-xl h-10 text-[18px]"
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
      )}
      {loading && <CustomLoadingModal />}
    </div>
  );
}
